import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import { apiClient } from '../api';
import { showToast } from '../utils/toaster';
import { useApp } from '../context/AppContext';
import PipeMonitoringTab from './EditPipePage/tabs/PipeMonitoringTab';
import OverviewTab from './EditPipePage/tabs/OverviewTab';
import LLMConfigTab from './EditPipePage/tabs/LLMConfigTab';
import RateLimitingTab from './EditPipePage/tabs/RateLimitingTab';
import CostTrackingTab from './EditPipePage/tabs/CostTrackingTab';
import PipeIntegrationsTab from './EditPipePage/tabs/PipeIntegrationsTab';
import { integrationIntegration } from '../components/connection-points/IntegrationsStep';
import { DeleteButton } from '../components/shared/DeleteButton';

interface ModelParameter {
  name: string;
  label: string;
  type: 'number' | 'string' | 'boolean' | 'select' | 'array' | 'object' | 'text' | 'multi-select';
  default: any;
  min?: number;
  max?: number;
  step?: number;
  description?: string;
  options?: string[];
  applies_to_models?: string[];
}

interface LLMModel {
  name: string;
  value: string;
  cost_per_1k_input: number;
  cost_per_1k_output: number;
  model_parameters?: ModelParameter[];
  rate_limiting?: {
    can_disable: boolean;
    can_change: boolean;
    requests_per_minute: {
      min: number;
      max: number;
      default: number;
    };
    tokens_per_minute: {
      min: number;
      max: number;
      default: number;
    };
  };
}

interface LLMEndpoint {
  name: string;
  path: string;
  description: string;
  is_default?: boolean;
  endpoint_parameters?: ModelParameter[];
}

interface LLMProvider {
  name: string;
  enum_value: string;
  endpoints: LLMEndpoint[];
  icon: string;
  api_key_label: string;
  models: LLMModel[];
}

interface Secret {
  id: string;
  name: string;
  secret_type?: string;
}

interface PipesConfig {
  llm?: {
    paths?: Array<{ id: string; name: string; prefix: string }>;
    providers?: LLMProvider[];
  };
  mcp_proxies?: {
    paths?: Array<{ id: string; name: string; prefix: string }>;
  };
}

interface PipeConfig {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  pipe_type: {
    LLM?: {
      provider: string;
      model: string;
      endpoint_url: string;
      api_key_secret_id?: string;
      max_tokens?: number;
      temperature?: number;
    };
  };
  rate_limit?: {
    enabled?: boolean;
    requests_per_minute?: number;
    tokens_per_minute?: number;
  };
  cost_tracking?: {
    enabled: boolean;
    input_price_per_1k?: number;
    output_price_per_1k?: number;
  };
  override_policy?: Record<string, string>;
  created_at: string;
  updated_at?: string;
}

const EditPipePage: React.FC = () => {
  const navigate = useNavigate();
  const { pipeId } = useParams<{ pipeId: string }>();
  const { state, actions } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Toaster helper with pipe-specific durations
  const showPipeToast = (type: 'success' | 'error' | 'loading', text: string) => {
    const duration = type === 'error' ? 8000 : (type === 'success' ? 2000 : 10000);
    showToast(type, text, { autoRemove: type !== 'loading', duration });
  };
  const [llmProviders, setLlmProviders] = useState<LLMProvider[]>([]);
  const [secrets, setSecrets] = useState<Secret[]>([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('basic');
  const [isModified, setIsModified] = useState(false);
  const [routingConfig, setRoutingConfig] = useState<{
    available_listen_addresses: string[];
    pipe_path_prefix: Array<{ id: string; name: string; prefix: string }>;
  } | null>(null);
  const [selectedPrefix, setSelectedPrefix] = useState<string>('');
  const [customPath, setCustomPath] = useState<string>('');
  const [integrations, setIntegrations] = useState<integrationIntegration[]>([]);
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    enabled: true,
    listen_address: '',
    route: '',
    provider: '',
    model: '',
    endpoint_url: '',
    api_key_secret_id: '',
    model_parameters: {} as Record<string, any>, // Dynamic parameters based on selected model
    rate_limit_enabled: true,
    requests_per_minute: 60,
    tokens_per_minute: 100000,
    cost_tracking_enabled: true,
    input_price_per_1k: 0,
    output_price_per_1k: 0,
    override_policy: {} as Record<string, string>,
    created_at: '',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadData = async () => {
      await loadPipesConfig();
      await loadRoutingConfig();
      await loadSecrets();
      if (pipeId) {
        await loadPipe();
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeId]);

  // Set dashboard filters when on a pipe page (for background data loading)
  useEffect(() => {
    if (!pipeId || pipeId === 'new') {
      // Clear filters for new pipe creation
      actions.setDashboardFilters(null);
      return;
    }
    
    // Always set filter for this pipe (to preload data in background)
    // But only if it's not already set to avoid unnecessary resets
    if (state.dashboardFilters?.pipe_id !== pipeId) {
      actions.setDashboardFilters({ pipe_id: pipeId });
    }
    
    // Don't clear filters on unmount - the next page will set its own filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeId]); // Only depend on pipeId, not activeTab

  // Switch active view based on which tab is displayed
  useEffect(() => {
    if (!pipeId || pipeId === 'new') {
      return;
    }
    
    // When on a pipe page, always use filtered view (all tabs need pipe-specific data)
    // The filter is set above, so the data is filtered to this pipe
    actions.setActiveView('filtered');
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pipeId]); // Switch view when tab changes

  // Parse route into prefix and custom path when data is ready
  useEffect(() => {
    // Skip if no pipe loaded yet or no routing config
    if (!pipeId || !formData.route || !routingConfig) {
      return;
    }

    // Skip if already parsed (selectedPrefix is set)
    if (selectedPrefix) {
      return;
    }

    const route = formData.route;
    let parsedPrefix = '';
    let parsedCustomPath = '';

    if (route && routingConfig.pipe_path_prefix) {
      // Find matching prefix
      for (const prefixInfo of routingConfig.pipe_path_prefix) {
        if (route.startsWith(prefixInfo.prefix + '/')) {
          parsedPrefix = prefixInfo.prefix;
          parsedCustomPath = route.substring(prefixInfo.prefix.length + 1); // +1 to skip the '/'
          break;
        }
      }
    }

    setSelectedPrefix(parsedPrefix);
    setCustomPath(parsedCustomPath);
  }, [pipeId, formData.route, routingConfig, selectedPrefix]);

  // Add keyboard shortcut support for save (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyboardSave = (event: KeyboardEvent) => {
      // Check for Ctrl+S (Windows/Linux) or Cmd+S (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault(); // Prevent browser's default save behavior
        
        // Only trigger save if there are changes and not already saving
        if (isModified && !saving) {
          handleSave();
        }
      }
    };

    // Add event listener when component mounts
    document.addEventListener('keydown', handleKeyboardSave);

    // Cleanup: remove event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyboardSave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModified, saving]);

  
  const loadPipesConfig = async () => {
    try {
      const response = await apiClient.get('/pipes/config');
      const config: PipesConfig = response.data;
      
      if (config.llm?.providers && config.llm.providers.length > 0) {
        setLlmProviders(config.llm.providers);
      }
    } catch (error) {
      console.error('Failed to load pipes config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const loadRoutingConfig = async () => {
    try {
      const response = await fetch('/api/v1/config/channel-routing');
      if (!response.ok) {
        throw new Error('Failed to fetch routing configuration');
      }
      const config = await response.json();
      setRoutingConfig(config);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to load routing configuration');
    }
  };

  const loadSecrets = async () => {
    try {
      const response = await apiClient.get('/secrets/');
      const allSecrets = response.data || [];
      const apiKeySecrets = allSecrets.filter((s: any) => s.secret_type === 'ApiKey');
      setSecrets(apiKeySecrets);
    } catch (error) {
      console.error('Failed to load secrets:', error);
    }
  };

  const loadPipe = async () => {
    try {
      const response = await fetch(`/api/v1/pipes/${pipeId}`);
      if (!response.ok) {
        throw new Error('Failed to load pipe');
      }
      
      const pipe: PipeConfig = await response.json();
      
      // The backend returns pipe_type with 'type' and 'provider' fields
      const llmConfig = (pipe.pipe_type as any).provider;
      
      // The backend returns the provider config with provider_type field
      // Extract the provider enum value from the nested structure
      let providerEnumValue = '';
      let modelValue = '';
      let endpointUrl = '';
      let apiKeySecretId = '';
      
      if (llmConfig) {
        // Handle different provider structures
        const providerConfig = llmConfig as any;
        
        // Extract provider type
        providerEnumValue = providerConfig.provider_type || providerConfig.provider || '';
        
        // Extract model based on provider type
        if (providerEnumValue === 'azure_openai') {
          modelValue = providerConfig.deployment_name || '';
          endpointUrl = providerConfig.endpoint || '';
          apiKeySecretId = providerConfig.api_key_secret || '';
        } else if (providerEnumValue === 'bedrock') {
          modelValue = providerConfig.model || '';
          apiKeySecretId = providerConfig.access_key_secret || '';
        } else {
          modelValue = providerConfig.model || '';
          endpointUrl = providerConfig.base_url || providerConfig.endpoint_url || '';
          apiKeySecretId = providerConfig.api_key_secret || providerConfig.api_key_secret_id || '';
        }
      }
      
      // Extract model parameters from llmConfig (exclude known system fields)
      // Note: We don't exclude max_tokens, temperature, etc. as they're dynamic model parameters
      const systemFields = ['provider', 'provider_type', 'model', 'deployment_name', 'endpoint', 'endpoint_url', 'base_url', 
                            'api_key_secret', 'api_key_secret_id', 'access_key_secret', 'region', 'api_version'];
      const modelParameters: Record<string, any> = {};
      if (llmConfig) {
        Object.keys(llmConfig).forEach(key => {
          if (!systemFields.includes(key)) {
            modelParameters[key] = (llmConfig as any)[key];
          }
        });
      }
      
      // If endpoint_url is empty, find the default endpoint from gateway config
      if (!endpointUrl && llmProviders.length > 0) {
        const provider = llmProviders.find(p => p.enum_value === providerEnumValue);
        if (provider?.endpoints) {
          const defaultEndpoint = provider.endpoints.find(e => e.is_default) || provider.endpoints[0];
          if (defaultEndpoint) {
            endpointUrl = defaultEndpoint.path;
          }
        }
      }
      
      const formDataToSet = {
        id: pipe.id,
        name: pipe.name,
        description: pipe.description || '',
        enabled: pipe.enabled,
        listen_address: (pipe as any).listen_address || '',
        route: (pipe as any).route || '',
        provider: providerEnumValue,
        model: modelValue,
        endpoint_url: endpointUrl,
        api_key_secret_id: apiKeySecretId,
        model_parameters: modelParameters,
        rate_limit_enabled: pipe.rate_limit?.enabled ?? true, // Default to true if not specified
        requests_per_minute: pipe.rate_limit?.requests_per_minute || 60,
        tokens_per_minute: pipe.rate_limit?.tokens_per_minute || 100000,
        cost_tracking_enabled: pipe.cost_tracking?.enabled || false,
        input_price_per_1k: pipe.cost_tracking?.input_price_per_1k || 0,
        output_price_per_1k: pipe.cost_tracking?.output_price_per_1k || 0,
        override_policy: pipe.override_policy || {},
        created_at: pipe.created_at || '',
      };
      
      setFormData(formDataToSet);
      
      // Load integrations from pipe config
      if ((pipe as any).integrations) {
        setIntegrations((pipe as any).integrations);
      }
      
      setIsModified(false); // Reset modified flag after loading
    } catch (error) {
      console.error('Failed to load pipe:', error);
      alert('Failed to load pipe');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Use a callback to get the latest state at save time
    setFormData(currentFormData => {
      // Kick off the actual save with the current state
      setIntegrations(currentIntegrations => {
        performSave(currentFormData, currentIntegrations);
        return currentIntegrations;
      });
      
      // Return unchanged state
      return currentFormData;
    });
  };

  const performSave = async (currentFormData: typeof formData, currentIntegrations: integrationIntegration[]) => {
    setSaving(true);
    
    try {
      // Build provider config based on provider type
      let providerConfig: any = {
        provider_type: currentFormData.provider,
        model: currentFormData.model,
        ...currentFormData.model_parameters, // Spread dynamic parameters
      };

      // Add provider-specific fields
      if (currentFormData.provider === 'azure_openai') {
        providerConfig.endpoint = currentFormData.endpoint_url;
        providerConfig.api_key_secret = currentFormData.api_key_secret_id;
        providerConfig.api_version = '2024-02-15-preview';
      } else if (currentFormData.provider === 'bedrock') {
        providerConfig.access_key_secret = currentFormData.api_key_secret_id;
        providerConfig.region = 'us-east-1';
      } else {
        providerConfig.api_key_secret = currentFormData.api_key_secret_id;
        if (currentFormData.endpoint_url && currentFormData.endpoint_url !== 'https://api.openai.com/v1/chat/completions') {
          providerConfig.base_url = currentFormData.endpoint_url;
        }
      }

      const updatedPipe = {
        id: pipeId,
        name: currentFormData.name,
        description: currentFormData.description,
        enabled: currentFormData.enabled,
        listen_address: currentFormData.listen_address || undefined,
        route: currentFormData.route || undefined,
        pipe_type: {
          type: 'llm',
          provider: providerConfig,
        },
        rate_limit: {
          enabled: currentFormData.rate_limit_enabled,
          requests_per_minute: currentFormData.requests_per_minute,
          tokens_per_minute: currentFormData.tokens_per_minute,
        },
        cost_tracking: {
          enabled: currentFormData.cost_tracking_enabled,
          input_price_per_1k: currentFormData.input_price_per_1k !== undefined && currentFormData.input_price_per_1k !== null ? currentFormData.input_price_per_1k : undefined,
          output_price_per_1k: currentFormData.output_price_per_1k !== undefined && currentFormData.output_price_per_1k !== null ? currentFormData.output_price_per_1k : undefined,
        },
        override_policy: Object.keys(currentFormData.override_policy || {}).length > 0 ? currentFormData.override_policy : undefined,
        integrations: currentIntegrations,
        created_at: currentFormData.created_at,
      };

      const response = await fetch(`/api/v1/pipes/${pipeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedPipe),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update pipe: ${response.status} ${errorText}`);
      }

      // After successful save, reload the pipe to get the latest data from backend
      await loadPipe();
      
      setIsModified(false);
      showPipeToast('success', `Pipe "${currentFormData.name}" has been successfully saved.`);
    } catch (error) {
      console.error('Failed to update pipe:', error);
      showPipeToast('error', `Failed to update pipe: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    
    setSaving(true);
    try {
      const response = await fetch(`/api/v1/pipes/${pipeId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete pipe');
      }

      showPipeToast('success', `Pipe "${formData.name}" has been successfully deleted.`);
      navigate('/pipes');
    } catch (error) {
      console.error('Failed to delete pipe:', error);
      showPipeToast('error', `Failed to delete pipe: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      return { ...prev, [field]: value };
    });
    setIsModified(true);
  };

  const selectedProvider = llmProviders.find(p => p.enum_value === formData.provider);
  const selectedModel = selectedProvider?.models.find(m => m.value === formData.model);
  const selectedEndpoint = selectedProvider?.endpoints.find(e => e.path === formData.endpoint_url);

  // Populate default model parameters when model changes
  // Track the current model to detect when it changes
  const [initializedModelParams, setInitializedModelParams] = React.useState<string>('');
  
  useEffect(() => {
    if (!selectedModel?.model_parameters) {
      // If selected model has no parameters, clear the model_parameters
      if (Object.keys(formData.model_parameters || {}).length > 0) {
        setFormData(prev => ({
          ...prev,
          model_parameters: {}
        }));
      }
      return;
    }
    
    const modelKey = `${formData.provider}_${formData.model}`;
    
    // Only initialize if the model has changed (not the same as last time)
    if (initializedModelParams === modelKey) {
      return;
    }
    
    // Model has changed - initialize parameters for the new model
    if (selectedModel.model_parameters.length > 0) {
      const newParams: Record<string, any> = {};
      selectedModel.model_parameters.forEach(param => {
        // Always use defaults when switching models
        newParams[param.name] = param.default;
      });
      
      setFormData(prev => ({
        ...prev,
        model_parameters: newParams
      }));
      
      // Mark this model as initialized
      setInitializedModelParams(modelKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel, formData.provider, formData.model]);

  // Set default endpoint if empty and provider is selected
  useEffect(() => {
    if (!formData.endpoint_url && formData.provider && llmProviders.length > 0) {
      const provider = llmProviders.find(p => p.enum_value === formData.provider);
      if (provider?.endpoints) {
        const defaultEndpoint = provider.endpoints.find(e => e.is_default) || provider.endpoints[0];
        if (defaultEndpoint) {
          setFormData(prev => ({
            ...prev,
            endpoint_url: defaultEndpoint.path
          }));
        }
      }
    }
  }, [formData.provider, formData.endpoint_url, llmProviders]);

  if (loading || configLoading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading pipe configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card shadow mb-4 channel-editor-card">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <div>
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-brain"></i> Edit Pipe: {formData.name}
              {formData.enabled ? (
                <span className="badge badge-success ml-2">Enabled</span>
              ) : (
                <span className="badge badge-secondary ml-2">Disabled</span>
              )}
              {formData.provider && (
                <span className="badge badge-info ml-2">{formData.provider}</span>
              )}
            </h6>
          </div>
          <div>
            <button
              type="button"
              className={`btn btn-sm btn-primary mr-2 ${!isModified || saving ? 'disabled' : ''}`}
              onClick={handleSave}
              disabled={!isModified || saving}
              title="Save changes (Ctrl+S / Cmd+S)"
            >
              <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'} mr-1`}></i>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <DeleteButton
              onDelete={handleDelete}
              variant="danger"
              disabled={saving}
            >
              Delete
            </DeleteButton>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => navigate('/pipes')}
              title="Close without saving"
            >
              <i className="fas fa-times mr-1"></i>
              Close
            </button>
          </div>
        </div>

        <div className="card-body-channel-editor">
          <Tabs
            activeKey={activeTab}
            onSelect={(key) => setActiveTab(key as string)}
            className="mb-3 custom-channel-tabs"
          >
            {/* Overview Tab */}
            <Tab eventKey="basic" title={<><i className="fas fa-info-circle"></i> Overview</>}>
              <OverviewTab
                formData={formData}
                handleInputChange={handleInputChange}
                routingConfig={routingConfig}
                selectedPrefix={selectedPrefix}
                setSelectedPrefix={setSelectedPrefix}
                customPath={customPath}
                setCustomPath={setCustomPath}
                setIsModified={setIsModified}
              />
            </Tab>

            {/* LLM Config Tab */}
            <Tab eventKey="llm" title={<><i className="fas fa-brain"></i> LLM Config</>}>
              <LLMConfigTab
                formData={formData}
                handleInputChange={handleInputChange}
                llmProviders={llmProviders}
                selectedProvider={selectedProvider || null}
                selectedModel={selectedModel || null}
                selectedEndpoint={selectedEndpoint || null}
                secrets={secrets}
                setIsModified={setIsModified}
              />
            </Tab>

            {/* Rate Limiting Tab */}
            <Tab eventKey="ratelimit" title={<><i className="fas fa-tachometer-alt"></i> Rate Limiting</>}>
              <RateLimitingTab
                formData={formData}
                handleInputChange={handleInputChange}
                selectedModel={selectedModel || null}
              />
            </Tab>

            {/* Cost Tracking Tab */}
            <Tab eventKey="cost" title={<><i className="fas fa-dollar-sign"></i> Cost Tracking</>}>
              <CostTrackingTab
                formData={formData}
                handleInputChange={handleInputChange}
              />
            </Tab>

            {/* Monitoring Tab */}
            {pipeId && (
              <Tab eventKey="monitoring" title={<><i className="fas fa-chart-line"></i> Monitoring</>}>
                <PipeMonitoringTab 
                  pipeId={pipeId}
                  pipeName={formData.name}
                  state={state}
                  actions={actions}
                />
              </Tab>
            )}

            {/* Integrations Tab */}
            {pipeId && pipeId !== 'new' && (
              <Tab eventKey="integrations" title={<><i className="fas fa-puzzle-piece"></i> Integrations</>}>
                <PipeIntegrationsTab
                  pipeId={pipeId}
                  integrations={integrations}
                  onIntegrationsChange={(newIntegrations: integrationIntegration[]) => {
                    setIntegrations(newIntegrations);
                    setIsModified(true);
                  }}
                  isModified={isModified}
                  isSaving={saving}
                />
              </Tab>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default EditPipePage;
